import { Button, Card, DataList, Flex, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSwitchInput } from 'components/common/form/switch';
import { CommonTextInput } from 'components/common/form/text';
import { HitRecapDiamond } from 'components/common/pitch-recap/diamond-view';
import useLatest from 'hooks/useLatestRef';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  IBuildHitPayload,
  IBuildHitResult,
  ISHHitDetails,
} from 'lib_ts/interfaces/session-hits/i-hit-details';
import { clone, first, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { PythonService } from 'services/python.service';

const LegacyResultValue = ({
  value,
  show,
}: {
  value?: Nullable<string | number>;
  show?: boolean;
}) => {
  if (!show) {
    return null;
  }

  if (isNil(value)) {
    return null;
  }

  return (
    <Text style={{ fontStyle: 'italic' }} ml="1">
      ({value})
    </Text>
  );
};

/**
 * The build-hits route replies 200 with an empty body when the python service
 * returns nothing, which is the norm on a local stack. Deriving the results
 * here keeps this internal demo page populated instead of blank.
 */
const mockBuildHitResults = (
  hits: Partial<ISHHitDetails>[]
): IBuildHitResult[] => {
  console.warn('FeatureDemo: no hit results from python, mocking them locally');

  return hits.map((hit, index) => {
    const o: IBuildHitResult = {
      index: index,
      distanceFT: hit.distanceFT ?? 0,
      // python corrects for drift, the launch angle is a close enough stand-in
      hLandingDEG: hit.hLaunchDEG ?? 0,
      outcome: HitHelper.getOutcomeLegacy(hit),
      xbacon: hit.xbacon ?? null,
      xwobacon: hit.xwobacon ?? null,
      outcomeProbs: {},
      usedExitSpeedMPH: 0,
      exitOmegaRPM: null,
      pathPoints: null,
      timeStep: null,
      landingTime: null,
      model: null,
    };

    return o;
  });
};

interface IHitDetailsComparison {
  result: ISHHitDetails;
  legacyResult?: IBuildHitResult;
}

export const FeatureDemoDiamondView = () => {
  const [showLegacyResults, setShowLegacyResults] = useState(false);
  const [loadingDemoHits, setLoadingDemoHits] = useState(false);
  const [demoHitDetails, setDemoHitDetails] = useState<IHitDetailsComparison[]>(
    []
  );

  // regenerate demo hits
  const generateDemoHits = useCallback(async () => {
    setLoadingDemoHits(true);

    const builtHitPayloads = ArrayHelper.getIntegerOptions(0, 4).map(() =>
      HitHelper.generateSampleData()
    );

    const response = await PythonService.getInstance().buildHits({
      payloads: builtHitPayloads.map((m, i) => {
        const o: IBuildHitPayload = {
          index: i,
          hit: m,
          pitchTrajectory: null,
        };

        return o;
      }),
      // also return legacy results for comparison/testing
      compareLegacy: true,
    });

    const results = response?.results ?? mockBuildHitResults(builtHitPayloads);

    const resultsWithLegacy: IHitDetailsComparison[] = builtHitPayloads.map(
      (hit, i) => {
        const result = results[i];
        const legacyResult = response?.legacyResults?.[i];

        return {
          result: {
            ...hit,
            distanceFT: result?.distanceFT,
            hLandingDEG: result?.hLandingDEG,
            outcome: result?.outcome,
          },
          legacyResult,
        };
      }
    );

    setDemoHitDetails(resultsWithLegacy);

    setLoadingDemoHits(false);
  }, []);

  useEffect(() => {
    generateDemoHits();
  }, [generateDemoHits]);

  const [manualHitDetails, setManualHitDetails] =
    useState<IHitDetailsComparison>({
      result: HitHelper.generateSampleData(),
      legacyResult: undefined,
    });

  const [buildManualHit, setBuildManualHit] = useState(Date.now());

  const latestManualHit = useLatest(manualHitDetails);

  // listen to changes in manualHitDetails to update the outcome from the server
  useEffect(() => {
    const timeout = setTimeout(async () => {
      const response = await PythonService.getInstance().buildHits({
        payloads: [
          {
            index: 0,
            hit: latestManualHit.current.result,
            pitchTrajectory: null,
          },
        ],
        compareLegacy: true,
      });

      const result = first(
        response?.results ??
          mockBuildHitResults([latestManualHit.current.result])
      );

      if (!result) {
        return;
      }

      setManualHitDetails((prev) => {
        const nextDetails = clone(prev);
        const legacyResult = first(response?.legacyResults ?? []);

        nextDetails.result.distanceFT = result.distanceFT;
        nextDetails.result.hLandingDEG = result.hLandingDEG;
        nextDetails.result.outcome = result.outcome;
        nextDetails.result._mx_outcome = result.outcome;

        if (legacyResult) {
          nextDetails.legacyResult = legacyResult;
        }

        return nextDetails;
      });
    }, 1_000);

    return () => clearTimeout(timeout);
  }, [buildManualHit]);

  // MARK: render
  return (
    <ErrorBoundary identifier="FeatureDemoDiamondView">
      <Flex align="center" justify="between" gap="2" mb="2">
        <Button
          id="generate-hits"
          size="1"
          onClick={() => {
            if (loadingDemoHits) {
              return;
            }

            generateDemoHits();
          }}
        >
          Generate Hits
        </Button>

        <CommonSwitchInput
          id="show-legacy-results"
          name="showLegacyResults"
          label="Show Legacy Results"
          defaultChecked={showLegacyResults}
          onCheckedChange={(v) => setShowLegacyResults(v)}
          checked={showLegacyResults}
          size="1"
          reverse
        />
      </Flex>

      <Flex gap="2" className="DemoDiamondViews">
        {/* manual inputs to test specific cases */}
        <Flex direction="column" gap="2" style={{ width: '100%' }}>
          <Card>
            <HitRecapDiamond
              hitter="Demo Hitter"
              hitDetails={manualHitDetails.result}
            />
          </Card>

          <DataList.Root size="1" orientation="vertical">
            <DataList.Item>
              <DataList.Label>Exit (mph)</DataList.Label>
              <DataList.Value>
                <CommonTextInput
                  id="manual-hit-exit"
                  value={
                    isNil(manualHitDetails.result.exitMPH)
                      ? ''
                      : manualHitDetails.result.exitMPH.toFixed(0)
                  }
                  size="1"
                  onNumericChange={(n) => {
                    setManualHitDetails((prev) => ({
                      ...prev,
                      result: {
                        ...prev.result,
                        exitMPH: n,
                      },
                    }));

                    setBuildManualHit(Date.now());
                  }}
                />
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>H. Launch (deg)</DataList.Label>
              <DataList.Value>
                <CommonTextInput
                  id="manual-hit-h-launch"
                  value={
                    isNil(manualHitDetails.result.hLaunchDEG)
                      ? ''
                      : manualHitDetails.result.hLaunchDEG.toFixed(2)
                  }
                  size="1"
                  onNumericChange={(n) => {
                    setManualHitDetails((prev) => ({
                      ...prev,
                      result: {
                        ...prev.result,
                        hLaunchDEG: n,
                      },
                    }));

                    setBuildManualHit(Date.now());
                  }}
                />
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>V. Launch (deg)</DataList.Label>
              <DataList.Value>
                <CommonTextInput
                  id="manual-hit-v-launch"
                  value={
                    isNil(manualHitDetails.result.vLaunchDEG)
                      ? ''
                      : manualHitDetails.result.vLaunchDEG.toFixed(2)
                  }
                  size="1"
                  onNumericChange={(n) => {
                    setManualHitDetails((prev) => ({
                      ...prev,
                      result: {
                        ...prev.result,
                        vLaunchDEG: n,
                      },
                    }));

                    setBuildManualHit(Date.now());
                  }}
                />
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>Distance (ft)</DataList.Label>
              <DataList.Value>
                {manualHitDetails.result.distanceFT?.toFixed(0)}
                <LegacyResultValue
                  value={manualHitDetails.legacyResult?.distanceFT?.toFixed(0)}
                  show={showLegacyResults}
                />
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>H. Landing (deg)</DataList.Label>
              <DataList.Value>
                {manualHitDetails.result.hLandingDEG?.toFixed(2) ?? '--'}
                <LegacyResultValue
                  value={
                    manualHitDetails.legacyResult?.hLandingDEG?.toFixed(2) ??
                    '--'
                  }
                  show={showLegacyResults}
                />
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>Python Outcome</DataList.Label>
              <DataList.Value>
                {manualHitDetails.result._mx_outcome}
                <LegacyResultValue
                  value={manualHitDetails.legacyResult?.outcome}
                  show={showLegacyResults}
                />
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>
        </Flex>

        {demoHitDetails.map(({ result: hit, legacyResult }, i) => (
          <Flex
            key={`demo-hit-${i}`}
            direction="column"
            gap="2"
            style={{ width: '100%' }}
          >
            <Card>
              <HitRecapDiamond hitter="Demo Hitter" hitDetails={hit} />
            </Card>

            <DataList.Root size="1" orientation="vertical">
              <DataList.Item>
                <DataList.Label>Exit (mph)</DataList.Label>
                <DataList.Value>{hit.exitMPH?.toFixed(1)}</DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>H. Launch (deg)</DataList.Label>
                <DataList.Value>{hit.hLaunchDEG?.toFixed(2)}</DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>V. Launch (deg)</DataList.Label>
                <DataList.Value>{hit.vLaunchDEG?.toFixed(2)}</DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Distance (ft)</DataList.Label>
                <DataList.Value>
                  {hit.distanceFT?.toFixed(0)}
                  <LegacyResultValue
                    value={legacyResult?.distanceFT?.toFixed(0)}
                    show={showLegacyResults}
                  />
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>H. Landing (deg)</DataList.Label>
                <DataList.Value>
                  {hit.hLandingDEG?.toFixed(2) ?? '--'}
                  <LegacyResultValue
                    value={legacyResult?.hLandingDEG?.toFixed(2)}
                    show={showLegacyResults}
                  />
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Python Outcome</DataList.Label>
                <DataList.Value>
                  {hit._mx_outcome}
                  <LegacyResultValue
                    value={legacyResult?.outcome}
                    show={showLegacyResults}
                  />
                </DataList.Value>
              </DataList.Item>
            </DataList.Root>
          </Flex>
        ))}
      </Flex>
    </ErrorBoundary>
  );
};
